import { HandCoins } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { REIMB_PERMS, usePayReimbursement, useReimbursements, type Reimbursement } from '../crm/expenses'
import { fmtDate, fmtMoney } from '../shared/format'
import { useAccounts } from './api'
import { Field } from './components/FormBits'
import { toastError } from './ui'

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' })

/** Personel geri ödemeleri: onaylanan nakit / şahsi kart müşteri giderleri (335 Personele Borçlar) kasa ya da bankadan ödenir. */
export function ReimbursementsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [status, setStatus] = useState<'open' | 'paid'>('open')
  const q = useReimbursements(status)
  const [paying, setPaying] = useState<Reimbursement | null>(null)
  const rows = q.data ?? []
  const total = rows.reduce((n, r) => n + Number(r.amount), 0)
  return (
    <>
      <PageHeader eyebrow={t('nav.groups.finance')} title={t('finance.reimbursements.title')} description={t('finance.reimbursements.subtitle')} />
      <div className="space-y-3">
        <Tabs value={status} onValueChange={(v) => setStatus(v as 'open' | 'paid')}>
          <TabsList size="sm">
            <TabsTrigger value="open">{t('finance.reimbursements.open')}</TabsTrigger>
            <TabsTrigger value="paid">{t('finance.reimbursements.paid')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <section className="rounded-xl border bg-card shadow-card">
          {q.isError ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          ) : q.isLoading ? (
            <Skeleton className="m-4 h-40" />
          ) : !rows.length ? (
            <EmptyState icon={HandCoins} title={t('finance.reimbursements.empty')} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>{t('finance.reimbursements.date')}</TH>
                    <TH>{t('finance.reimbursements.staff')}</TH>
                    <TH>{t('finance.reimbursements.description')}</TH>
                    <TH>{t('finance.reimbursements.status')}</TH>
                    <TH className="text-right">{t('finance.reimbursements.amount')}</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <TR key={r.id}>
                      <TD className="tabular-nums">{fmtDate(r.status === 'paid' ? r.paidAt : r.createdAt)}</TD>
                      <TD className="font-medium">{r.userName}</TD>
                      <TD className="max-w-80 truncate">{r.description}</TD>
                      <TD>
                        <Badge tone={r.status === 'paid' ? 'success' : 'warning'}>{t(`finance.reimbursements.${r.status}`)}</Badge>
                      </TD>
                      <TD className="text-right font-medium tabular-nums">{fmtMoney(r.amount)}</TD>
                      <TD className="text-right">
                        {r.status === 'open' && can(REIMB_PERMS.pay) ? (
                          <Button size="sm" onClick={() => setPaying(r)}>
                            {t('finance.reimbursements.pay')}
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                  <TR className="border-t-2 font-semibold">
                    <TD colSpan={4}>{t('finance.reports.totals')}</TD>
                    <TD className="text-right tabular-nums">{fmtMoney(total.toFixed(2))}</TD>
                    <TD />
                  </TR>
                </TBody>
              </Table>
            </div>
          )}
        </section>
      </div>
      {paying ? <PayModal item={paying} onClose={() => setPaying(null)} /> : null}
    </>
  )
}

function PayModal({ item, onClose }: { item: Reimbursement; onClose: () => void }) {
  const { t } = useTranslation()
  const accounts = useAccounts()
  const pay = usePayReimbursement()
  const [accountId, setAccountId] = useState('')
  const [date, setDate] = useState(today)
  const options = (accounts.data ?? []).filter((a) => a.active && (a.type === 'cash' || a.type === 'bank') && (!a.currency || a.currency === 'TRY'))
  return (
    <Modal
      open
      size="md"
      onOpenChange={(o) => !o && onClose()}
      icon={<HandCoins />}
      title={t('finance.reimbursements.payTitle', { name: item.userName })}
      description={t('finance.reimbursements.payHint', { amount: fmtMoney(item.amount) })}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!accountId || !date || pay.isPending}
            onClick={() =>
              pay.mutate(
                { id: item.id, accountId, date },
                {
                  onSuccess: () => {
                    toast.success(t('finance.reimbursements.paidToast'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('finance.reimbursements.pay')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field id="rb-acc" label={t('finance.reimbursements.account')}>
          <Select id="rb-acc" value={accountId} onChange={setAccountId} options={[{ value: '', label: t('common.select') }, ...options.map((a) => ({ value: a.id, label: `${a.name} · ${fmtMoney(a.balance)}` }))]} />
        </Field>
        <Field id="rb-date" label={t('finance.reimbursements.date')}>
          <DateField id="rb-date" value={date} onChange={setDate} clearable={false} />
        </Field>
      </div>
    </Modal>
  )
}
